import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import swal from 'sweetalert';
import {
	getBookableJourneyAdmin,
	createBookableJourney,
	updateBookableJourney,
} from '../../../api/bookableJourneyApi';
import AvailableMonthsPicker from '../cms/AvailableMonthsPicker';
import { seasonsLabel, storedAvailableMonths } from '../../../utils/bookingAvailability';
import '../../../styles/admin-cms.scss';

const EMPTY = {
	slug: '',
	title: '',
	active: true,
	group_enabled: true,
	private_enabled: true,
	vendor_total_price: '',
	currency_code: 'USD',
	duration_days: '',
	origin: 'Kathmandu',
	destination: '',
	min_group_size: 6,
	max_group_size: 12,
	price_type: 'per_person',
	group_markup_percentage: '',
	group_interest_percentage: '',
	private_markup_percentage: '',
	private_interest_percentage: '',
	available_months: [],
	priority: 0,
	guided_journey_slug: '',
};

function optionalNumber(value) {
	return value !== '' && value != null ? Number(value) : null;
}

function formatOptionalNumber(value) {
	return value != null && value !== '' ? String(value) : '';
}

export default function BookableJourneyAdminForm() {
	const { slug } = useParams();
	const history = useHistory();
	const isEdit = Boolean(slug);
	const [form, setForm] = useState(EMPTY);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!isEdit) return;
		getBookableJourneyAdmin(slug)
			.then((res) => {
				const row = res.data?.bookable_journey || res.data;
				setForm({
					...EMPTY,
					...row,
					vendor_total_price: row.vendor_total_price != null ? String(row.vendor_total_price) : '',
					duration_days: row.duration_days != null ? String(row.duration_days) : '',
					group_markup_percentage: formatOptionalNumber(row.group_markup_percentage),
					group_interest_percentage: formatOptionalNumber(row.group_interest_percentage),
					private_markup_percentage: formatOptionalNumber(row.private_markup_percentage),
					private_interest_percentage: formatOptionalNumber(row.private_interest_percentage),
					available_months: storedAvailableMonths(row),
					guided_journey_slug: row.guided_journey_slug || '',
				});
			})
			.catch(() => swal('Error', 'Could not load booking catalog item', 'error'));
	}, [isEdit, slug]);

	const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!form.title.trim()) {
			swal('Missing title', 'Title is required.', 'error');
			return;
		}
		if (!form.vendor_total_price) {
			swal('Missing price', 'Vendor total price is required for pricing to work.', 'error');
			return;
		}
		if (!form.duration_days) {
			swal('Missing duration', 'Duration (days) is required for departures.', 'error');
			return;
		}
		setSaving(true);
		const payload = {
			...form,
			vendor_total_price: Number(form.vendor_total_price),
			duration_days: Number(form.duration_days),
			min_group_size: Number(form.min_group_size) || 6,
			max_group_size: Number(form.max_group_size) || 12,
			priority: Number(form.priority) || 0,
			group_markup_percentage: optionalNumber(form.group_markup_percentage),
			group_interest_percentage: optionalNumber(form.group_interest_percentage),
			private_markup_percentage: optionalNumber(form.private_markup_percentage),
			private_interest_percentage: optionalNumber(form.private_interest_percentage),
			available_months: Array.isArray(form.available_months) ? form.available_months.map(Number) : [],
			guided_journey_slug: form.guided_journey_slug.trim() || null,
		};
		try {
			if (isEdit) await updateBookableJourney(slug, payload);
			else await createBookableJourney(payload);
			swal('Saved', 'Booking catalog item saved.', 'success');
			history.push('/admin/booking_catalog');
		} catch (err) {
			swal('Error', err.response?.data?.errors?.[0] || 'Could not save', 'error');
		} finally {
			setSaving(false);
		}
	};

	const previewRow = { ...form, available_months_stored: form.available_months };

	return (
		<div className="admin-cms-form">
			<Link to="/admin/booking_catalog" className="admin-cms-back">← Booking catalog</Link>
			<header className="admin-cms-form__head">
				<h1>{isEdit ? `Edit: ${form.title || slug}` : 'Add bookable journey'}</h1>
				<p className="admin-cms-muted">
					Controls group/private dropdowns and pricing. Optional link to a guided journey page for marketing content.
				</p>
			</header>

			<form onSubmit={handleSubmit} className="admin-cms-form__body">
				<fieldset className="admin-cms-form__section">
					<legend>Basics</legend>
					<div className="admin-cms-form__row">
						<label><span>Title</span><input required value={form.title} onChange={(e) => onChange('title', e.target.value)} /></label>
						<label><span>Slug</span><input required value={form.slug} onChange={(e) => onChange('slug', e.target.value)} disabled={isEdit} placeholder="everest-base-camp" /></label>
					</div>
					<div className="admin-cms-form__row">
						<label><span>Priority</span><input type="number" min="0" value={form.priority} onChange={(e) => onChange('priority', e.target.value)} /></label>
						<label><span>Guided journey slug (optional)</span><input value={form.guided_journey_slug} onChange={(e) => onChange('guided_journey_slug', e.target.value)} placeholder="Link to /journeys/… page if published" /></label>
					</div>
					<div className="admin-cms-form__row">
						<label className="admin-cms-form__check"><input type="checkbox" checked={!!form.active} onChange={(e) => onChange('active', e.target.checked)} /> Active in dropdowns</label>
						<label className="admin-cms-form__check"><input type="checkbox" checked={form.group_enabled !== false} onChange={(e) => onChange('group_enabled', e.target.checked)} /> Group departures</label>
						<label className="admin-cms-form__check"><input type="checkbox" checked={form.private_enabled !== false} onChange={(e) => onChange('private_enabled', e.target.checked)} /> Private tours</label>
					</div>
				</fieldset>

				<fieldset className="admin-cms-form__section">
					<legend>Pricing & trip details</legend>
					<div className="admin-cms-form__row">
						<label><span>Vendor total price</span><input type="number" min="0" step="0.01" required value={form.vendor_total_price} onChange={(e) => onChange('vendor_total_price', e.target.value)} /></label>
						<label>
							<span>Currency</span>
							<select value={form.currency_code} onChange={(e) => onChange('currency_code', e.target.value)}>
								<option value="USD">USD</option>
								<option value="NPR">NPR</option>
							</select>
						</label>
						<label><span>Duration (days)</span><input type="number" min="1" required value={form.duration_days} onChange={(e) => onChange('duration_days', e.target.value)} /></label>
					</div>
					<div className="admin-cms-form__row">
						<label><span>Origin</span><input required value={form.origin} onChange={(e) => onChange('origin', e.target.value)} /></label>
						<label><span>Destination</span><input required value={form.destination} onChange={(e) => onChange('destination', e.target.value)} /></label>
					</div>
					<div className="admin-cms-form__row">
						<label><span>Min group size</span><input type="number" min="1" value={form.min_group_size} onChange={(e) => onChange('min_group_size', e.target.value)} /></label>
						<label><span>Max group size</span><input type="number" min="1" value={form.max_group_size} onChange={(e) => onChange('max_group_size', e.target.value)} /></label>
						<label>
							<span>Price type</span>
							<select value={form.price_type} onChange={(e) => onChange('price_type', e.target.value)}>
								<option value="per_person">Per person</option>
								<option value="per_group">Per group</option>
							</select>
						</label>
					</div>
				</fieldset>

				<fieldset className="admin-cms-form__section">
					<legend>Markup overrides (optional)</legend>
					<p className="admin-cms-form__hint">Leave blank to use platform defaults from Settings.</p>
					<div className="admin-cms-form__row">
						<label><span>Group markup %</span><input value={form.group_markup_percentage} onChange={(e) => onChange('group_markup_percentage', e.target.value)} /></label>
						<label><span>Group deposit %</span><input value={form.group_interest_percentage} onChange={(e) => onChange('group_interest_percentage', e.target.value)} /></label>
					</div>
					<div className="admin-cms-form__row">
						<label><span>Private markup %</span><input value={form.private_markup_percentage} onChange={(e) => onChange('private_markup_percentage', e.target.value)} /></label>
						<label><span>Private deposit %</span><input value={form.private_interest_percentage} onChange={(e) => onChange('private_interest_percentage', e.target.value)} /></label>
					</div>
				</fieldset>

				<fieldset className="admin-cms-form__section">
					<legend>Available months</legend>
					<p className="admin-cms-form__hint">Preview: {seasonsLabel(previewRow)}</p>
					<AvailableMonthsPicker
						value={form.available_months}
						onChange={(months) => onChange('available_months', months)}
					/>
				</fieldset>

				<div className="admin-cms-form__actions">
					<button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
					<Link to="/admin/booking_catalog" className="btn btn-outline-secondary">Cancel</Link>
				</div>
			</form>
		</div>
	);
}
